import { fileExtensionRegex } from "@constants";
import {
  createContentResourceSignedUrl,
  deleteContentResource,
  updateContentResourceById,
  uploadContentResourceToSignedUrl,
} from "@services/http/members";
import { CreateSignedUrlInput, UploadSignedUrlInput } from "@_types/shared";
import { v4 as uuidv4 } from "uuid";

import { AxiosProgressEvent, isAxiosError } from "axios";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { ResourcesActions, ResourcesStoreProps } from "./ContentResources.types";

const initialState: ResourcesStoreProps = {
  isLoading: false,
  isUploading: false,
  isRenaming: false,
  contentResources: [],
};

export const useContentResourcesStore = create<ResourcesStoreProps & ResourcesActions>()(
  devtools((set, get) => ({
    ...initialState,

    uploadFile: async (file, toast, newName) => {
      const { contentResources, updateUploadProgress, updateResourceUuid } = get();
      const prevContentResources = contentResources;
      const newFileId = uuidv4();

      const onUploadProgress = (event: AxiosProgressEvent) => {
        const percentage = Math.round((100 * event.loaded) / (event.total || event.loaded));
        updateUploadProgress(newFileId, percentage);
      };

      const mimetype = file.type;
      const size = file.size;
      const originalname = newName || `${file.name.replaceAll(/\s/g, "")}`;

      const fileExtension = file.name.match(fileExtensionRegex);

      const newResource = {
        id: newFileId,
        file: "",
        uuid: newFileId,
        mimetype: fileExtension?.[0] || "",
        title: originalname.replace(fileExtensionRegex, ""),
        progress: 0,
      };

      set({
        contentResources: [...contentResources, newResource],
      });

      const bodyData: CreateSignedUrlInput = {
        mimetype,
        originalname,
        size,
      };

      const defaultErrorMessage =
        "Ocorreu um erro desconhecido durante o upload do arquivo, tente novamente mais tarde";

      try {
        set({ isUploading: true });

        const { data: response } = await createContentResourceSignedUrl(bodyData);
        const formData: UploadSignedUrlInput = { ...response.data, file };

        try {
          await uploadContentResourceToSignedUrl(response.url, formData, onUploadProgress);

          updateResourceUuid(newFileId, response.uuid, response.file);
          toast({
            title: "Sucesso",
            description: "Arquivo enviado com sucesso",
            type: "success",
          });
        } catch (error) {
          toast({
            title: "Ocorreu um erro",
            description: defaultErrorMessage,
            type: "error",
          });
        }
      } catch (error) {
        let errorMessage = defaultErrorMessage;
        set({ contentResources: prevContentResources });
        if (isAxiosError(error)) {
          errorMessage = error.response?.data.message || defaultErrorMessage;
        }
        toast({
          title: "Ocorreu um erro",
          description: errorMessage,
          type: "error",
        });
      } finally {
        set({ isUploading: false });
      }
    },

    deleteFile: async fileUuid => {
      const { contentResources } = get();
      set({ isLoading: true });
      try {
        await deleteContentResource(fileUuid);
        const filteredResources = contentResources.filter(resource => resource.uuid !== fileUuid);
        set({ contentResources: filteredResources });
      } catch (error) {
        if (isAxiosError(error)) throw new Error(error.response?.data?.message, {});
      } finally {
        set({ isLoading: false });
      }
    },

    renameFile: async (uuid, data) => {
      const { renameFileOnTextEdit } = get();
      try {
        await updateContentResourceById(uuid, data);
        renameFileOnTextEdit(uuid, data.title);
      } catch (error) {
        if (isAxiosError(error)) return error?.response?.data?.message;
      }
    },

    renameFileOnTextEdit: (uuid, newName) => {
      const { contentResources } = get();
      const updatedList = contentResources.map(content => {
        if (content.uuid === uuid) {
          return {
            ...content,
            title: newName,
          };
        }
        return content;
      });
      set({ contentResources: updatedList });
    },

    getResourcesOnStore: () => {
      const { contentResources } = get();
      const updatedUuids = contentResources.map(resource => ({
        uuid: resource.uuid,
        title: resource.title,
      }));
      return updatedUuids;
    },

    updateUploadProgress: (id, progress) => {
      const { contentResources } = get();
      const uploadingFile = contentResources.find(resource => resource.id === id);
      const otherFiles = contentResources.filter(resource => resource.id !== id);

      if (uploadingFile) {
        uploadingFile.progress = progress;
        set({ contentResources: [...otherFiles, uploadingFile] });
      }
    },

    updateResourceUuid: (id, uuid, file) => {
      const { contentResources } = get();
      const uploadedFile = contentResources.find(resource => resource.id === id);
      const otherFiles = contentResources.filter(resource => resource.id !== id);

      if (uploadedFile) {
        uploadedFile.uuid = uuid;
        uploadedFile.file = file;
        uploadedFile.progress = undefined;
        set({ contentResources: [...otherFiles, uploadedFile] });
      }
    },

    setResourcesOnStore: data => {
      const resources = data.map(resource => {
        const fileExtension = resource.file.match(fileExtensionRegex);
        return {
          ...resource,
          id: resource.uuid,
          mimetype: fileExtension?.[0].split("?")[0] || "",
        };
      });

      set({ contentResources: resources });
    },

    setIsRenaming: state => set({ isLoading: state }),

    cleanStore: () => set({ contentResources: [], isLoading: false, isRenaming: false, isUploading: false }),
  }))
);
